import { type ChangeEvent, type Dispatch, memo, type SetStateAction } from "react";
import "Components/ConfigLayout/Event/ActionSettings/ActionLightSettings/ActionLightSettings.less";
import {
	Checkbox, Icon, IconSize, IconWeight, Input, type SelectOption, Popover, Select
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { ActionState, LightEvents, LightSlice } from "Interfaces";
import { Link } from "react-router-dom";
import { FlickerFrequency } from "Data/Objects/Event";

type ActionLightSettingsProps = {
	whiteLight?: boolean;
	localActionState: [ ActionState, Dispatch<SetStateAction<ActionState>> ];
};

function ActionLightSettings(props: ActionLightSettingsProps) {
	const { localActionState: [ localActionSettings, setLocalActionSettings ], whiteLight } = props;
	const lightSlice: LightSlice = whiteLight ? "whiteLight" : "redAndBlueLight";
	const link = (
		<div>
			Go to <Link to="/setup/system/schedule">Schedule Preset</Link> to create a new preset.
		</div>
	);

	const flickerFrequencyOptions: SelectOption[] = [
		{
			label: FlickerFrequency.LOW,
			value: FlickerFrequency.LOW
		},
		{
			label: FlickerFrequency.MEDIUM,
			value: FlickerFrequency.MEDIUM
		},
		{
			label: FlickerFrequency.HIGH,
			value: FlickerFrequency.HIGH
		}
	];

	if (whiteLight) {
		flickerFrequencyOptions.unshift({
			label: FlickerFrequency.ALWAYS_ON,
			value: FlickerFrequency.ALWAYS_ON
		});
	}

	const handleScheduleChange = (scheduleName: string) => (event: ChangeEvent<HTMLInputElement>) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft[ lightSlice ].schedule[ scheduleName ] = event.currentTarget.checked;
		}));
	};

	const handleFlickerFrequencySelect = (event: LightEvents) => (flickerFrequency: string | null) => {
		if (!flickerFrequency) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft[ lightSlice ][ event ].flickerFrequency = flickerFrequency as FlickerFrequency;
		}));
	};

	const handleDurationUpdate = (event: LightEvents) => (duration: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft[ lightSlice ][ event ].duration = +duration;
		}));
	};

	const renderSchedule = (schedule: [ string, boolean ]) => {
		const [ scheduleName, checked ] = schedule;

		return (
			<Checkbox checked={ checked } key={ scheduleName } onChange={ handleScheduleChange(scheduleName) }>
				<span>{ scheduleName }</span>
			</Checkbox>
		);
	};

	return (
		<div className="light-settings">
			<div className="column">
				<div className="form-row header-row">
					<span>Event Type</span>
					<span>Flash Rate</span>
					<span>Duration</span>
				</div>
				<div className="form-row">
					<span>Alarm Event</span>
					<Select
						allowClear={ false }
						onSelect={ handleFlickerFrequencySelect("alarmEvent") }
						options={ flickerFrequencyOptions }
						value={ localActionSettings[ lightSlice ].alarmEvent.flickerFrequency }
					/>
					<Input
						hideCharMessage={ true }
						max={ 60 }
						min={ 1 }
						onUpdate={ handleDurationUpdate("alarmEvent") }
						type="number"
						value={ localActionSettings[ lightSlice ].alarmEvent.duration }
					/>
				</div>
				<div className="form-row">
					<span>Motion Event</span>
					<Select
						allowClear={ false }
						onSelect={ handleFlickerFrequencySelect("motionEvent") }
						options={ flickerFrequencyOptions }
						value={ localActionSettings[ lightSlice ].motionEvent.flickerFrequency }
					/>
					<Input
						hideCharMessage={ true }
						max={ 60 }
						min={ 1 }
						onUpdate={ handleDurationUpdate("motionEvent") }
						type="number"
						value={ localActionSettings[ lightSlice ].motionEvent.duration }
					/>
				</div>
				<div className="form-row">
					<span>Tamper Event</span>
					<Select
						allowClear={ false }
						onSelect={ handleFlickerFrequencySelect("tamperEvent") }
						options={ flickerFrequencyOptions }
						value={ localActionSettings[ lightSlice ].tamperEvent.flickerFrequency }
					/>
					<Input
						hideCharMessage={ true }
						max={ 60 }
						min={ 1 }
						onUpdate={ handleDurationUpdate("tamperEvent") }
						type="number"
						value={ localActionSettings[ lightSlice ].tamperEvent.duration }
					/>
				</div>
				<div className="form-row">
					<span>AI Analytics</span>
					<Select
						allowClear={ false }
						onSelect={ handleFlickerFrequencySelect("aiAnalytics") }
						options={ flickerFrequencyOptions }
						value={ localActionSettings[ lightSlice ].aiAnalytics.flickerFrequency }
					/>
					<Input
						hideCharMessage={ true }
						max={ 60 }
						min={ 1 }
						onUpdate={ handleDurationUpdate("aiAnalytics") }
						type="number"
						value={ localActionSettings[ lightSlice ].aiAnalytics.duration }
					/>
				</div>
			</div>
			<div className="column">
				<div className="schedule-title">
					<h4>Schedule</h4>
					<Popover content={ link }>
						<Icon name="circle-info" iconWeight={ IconWeight.REGULAR } size={ IconSize.SMALLER } />
					</Popover>
				</div>
				<div className="schedules">
					{ Object.entries(localActionSettings[ lightSlice ].schedule).map(renderSchedule) }
				</div>
			</div>
		</div>
	);
}

export default memo(ActionLightSettings);
